import React, { useCallback, useEffect, useRef, useState } from 'react';
import { adminService } from '../../api';
import Editor from '../../components/editor/Editor';
import { toEditorData } from '../../components/editor/editorData';
import './AdminContentPostsPage.css';

const emptyForm = { title: '', slug: '', summary: '', coverImageUrl: '', isPublished: false };

// Shared admin CRUD for News and Articles — same shape, distinguished by `type`.
const AdminContentPostsPage = ({ type, title }) => {
    const [items, setItems] = useState([]);
    const [totalPages, setTotalPages] = useState(1);
    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [busyId, setBusyId] = useState(null);

    const [editingId, setEditingId] = useState(null); // null = list, 'new' = create, id = edit
    const [form, setForm] = useState(emptyForm);
    const [editorData, setEditorData] = useState(null);
    const editorRef = useRef(null);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [formError, setFormError] = useState(null);

    const load = useCallback(() => {
        setLoading(true);
        setError(null);
        adminService.getContentPosts(type, { page, pageSize: 20, search: search || undefined })
            .then(({ data }) => {
                setItems(data.items);
                setTotalPages(data.totalPages);
            })
            .catch(() => setError('Не удалось загрузить список.'))
            .finally(() => setLoading(false));
    }, [type, page, search]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { setPage(1); setEditingId(null); }, [type]);

    const submitSearch = (e) => {
        e.preventDefault();
        setPage(1);
        setSearch(searchInput.trim());
    };

    const startCreate = () => {
        setForm(emptyForm);
        setEditorData({ blocks: [] });
        setFormError(null);
        setEditingId('new');
    };

    const startEdit = async (id) => {
        setFormError(null);
        setEditorData(null);
        setEditingId(id);
        try {
            const { data } = await adminService.getContentPost(id);
            setForm({
                title: data.title,
                slug: data.slug,
                summary: data.summary || '',
                coverImageUrl: data.coverImageUrl || '',
                isPublished: data.isPublished,
            });
            setEditorData(toEditorData(data.content));
        } catch {
            setFormError('Не удалось загрузить материал.');
        }
    };

    const cancelEdit = () => {
        setEditingId(null);
        setEditorData(null);
        setFormError(null);
    };

    const handleCoverUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            const { data } = await adminService.uploadContentPostCover(file);
            setForm(f => ({ ...f, coverImageUrl: data.url }));
        } catch {
            setFormError('Не удалось загрузить изображение.');
        } finally {
            setUploading(false);
        }
    };

    const save = async (e) => {
        e.preventDefault();
        setSaving(true);
        setFormError(null);
        try {
            const output = await editorRef.current?.save();
            if (!form.title.trim() || !output?.blocks?.length) {
                setFormError('Заполните заголовок и текст.');
                return;
            }
            const payload = {
                title: form.title.trim(),
                slug: form.slug.trim() || null,
                summary: form.summary.trim() || null,
                content: JSON.stringify(output),
                coverImageUrl: form.coverImageUrl || null,
                isPublished: form.isPublished,
            };
            if (editingId === 'new') {
                await adminService.createContentPost(type, payload);
            } else {
                await adminService.updateContentPost(editingId, payload);
            }
            setEditingId(null);
            setEditorData(null);
            load();
        } catch {
            setFormError('Не удалось сохранить.');
        } finally {
            setSaving(false);
        }
    };

    const remove = async (id) => {
        if (!window.confirm('Удалить материал?')) return;
        setBusyId(id);
        try {
            await adminService.deleteContentPost(id);
            setItems(prev => prev.filter(p => p.id !== id));
        } catch {
            alert('Не удалось удалить.');
        } finally {
            setBusyId(null);
        }
    };

    if (editingId !== null) {
        return (
            <div className="admin-content">
                <h1 className="admin-content__title">
                    {editingId === 'new' ? `Новый материал — ${title}` : `Редактирование — ${title}`}
                </h1>

                {formError && <div className="admin-content__error">{formError}</div>}

                <form className="admin-content__form" onSubmit={save}>
                    <label className="admin-content__label">
                        Заголовок
                        <input
                            className="admin-content__input"
                            value={form.title}
                            onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                        />
                    </label>

                    <label className="admin-content__label">
                        Слаг (необязательно, сгенерируется из заголовка)
                        <input
                            className="admin-content__input"
                            value={form.slug}
                            onChange={e => setForm(f => ({ ...f, slug: e.target.value }))}
                        />
                    </label>

                    <label className="admin-content__label">
                        Краткое описание
                        <textarea
                            className="admin-content__textarea admin-content__textarea--short"
                            value={form.summary}
                            onChange={e => setForm(f => ({ ...f, summary: e.target.value }))}
                        />
                    </label>

                    <div className="admin-content__label">
                        Текст
                        {editorData && <Editor key={editingId} ref={editorRef} data={editorData} placeholder="Начните писать..." />}
                    </div>

                    <div className="admin-content__label">
                        Обложка
                        <div className="admin-content__cover-row">
                            {form.coverImageUrl && (
                                <img src={form.coverImageUrl} alt="" className="admin-content__cover-preview" />
                            )}
                            <input type="file" accept="image/*" onChange={handleCoverUpload} disabled={uploading} />
                        </div>
                    </div>

                    <label className="admin-content__checkbox">
                        <input
                            type="checkbox"
                            checked={form.isPublished}
                            onChange={e => setForm(f => ({ ...f, isPublished: e.target.checked }))}
                        />
                        Опубликовано
                    </label>

                    <div className="admin-content__form-actions">
                        <button type="submit" className="admin-content__btn-save" disabled={saving}>
                            {saving ? 'Сохранение...' : 'Сохранить'}
                        </button>
                        <button type="button" className="admin-content__btn-cancel" onClick={cancelEdit}>
                            Отмена
                        </button>
                    </div>
                </form>
            </div>
        );
    }

    return (
        <div className="admin-content">
            <div className="admin-content__header">
                <h1 className="admin-content__title">{title}</h1>
                <button className="admin-content__btn-add" onClick={startCreate}>+ Добавить</button>
            </div>

            <form className="admin-content__search" onSubmit={submitSearch}>
                <input
                    className="admin-content__search-input"
                    placeholder="Поиск по заголовку..."
                    value={searchInput}
                    onChange={e => setSearchInput(e.target.value)}
                />
                <button type="submit" className="admin-content__search-btn">Найти</button>
            </form>

            {loading && <div className="admin-content__placeholder">Загрузка...</div>}
            {error && <div className="admin-content__placeholder">{error}</div>}
            {!loading && !error && items.length === 0 && (
                <div className="admin-content__placeholder">Ничего не найдено.</div>
            )}

            <div className="admin-content__list">
                {items.map(p => (
                    <div key={p.id} className="admin-content__card">
                        <div className="admin-content__card-main">
                            <div className="admin-content__card-title">{p.title}</div>
                            <div className="admin-content__meta">
                                <span className={`admin-content__status ${p.isPublished ? 'admin-content__status--published' : ''}`}>
                                    {p.isPublished ? 'Опубликовано' : 'Черновик'}
                                </span>
                                {p.publishedAt && <span> · {new Date(p.publishedAt).toLocaleDateString('ru-RU')}</span>}
                                <span> · {p.views} просм.</span>
                                {p.authorName && <span> · {p.authorName}</span>}
                            </div>
                        </div>
                        <div className="admin-content__card-actions">
                            <button className="admin-content__btn-edit" onClick={() => startEdit(p.id)}>Изменить</button>
                            <button
                                className="admin-content__btn-delete"
                                disabled={busyId === p.id}
                                onClick={() => remove(p.id)}
                            >
                                Удалить
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {totalPages > 1 && (
                <div className="admin-content__pagination">
                    <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Назад</button>
                    <span>{page} / {totalPages}</span>
                    <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>Далее</button>
                </div>
            )}
        </div>
    );
};

export default AdminContentPostsPage;
